import React from 'react';
import {
  GlobeAltIcon,
  TruckIcon,
  BuildingOffice2Icon,
  ClockIcon,
  UserGroupIcon,
  ExclamationTriangleIcon,
  ShieldCheckIcon,
  DocumentCheckIcon,
  CubeTransparentIcon,
  BuildingLibraryIcon,
  ChartBarIcon,
} from '@heroicons/react/24/outline';
import { SolutionPageTemplate } from '../../components/page-templates/MarketPageTemplates';

const painPoints = [
  {
    icon: DocumentCheckIcon,
    title: 'Regulatory Complexity',
    description: 'Different import rules, certifications, and duties across every market you operate in',
  },
  {
    icon: ClockIcon,
    title: 'Multi-Country Rollout Timelines',
    description: 'Global ICT deployments run on fixed schedules tied to business operations and SLAs',
  },
  {
    icon: UserGroupIcon,
    title: 'Fragmented Vendor Networks',
    description: 'Too many forwarders, brokers, and warehouses with no unified visibility or accountability',
  },
  {
    icon: ExclamationTriangleIcon,
    title: 'On-Site Delivery Risk',
    description: 'Equipment must arrive on time, configured correctly, and positioned at the right location',
  },
];

const solutions = [
  {
    icon: DocumentCheckIcon,
    title: 'Regulatory & Import Compliance',
    points: [
      'IOR/EOR services in 140+ countries',
      'Duties and taxes management',
      'Faster clearance and processing',
      'Market entry without local entity setup',
    ],
  },
  {
    icon: TruckIcon,
    title: 'Project-Based Freight & Consolidation',
    points: [
      'Consolidated shipments for multi-site rollouts',
      'Phased delivery aligned to deployment milestones',
      'Secure handling of enterprise ICT equipment',
      'Coordinated cross-border movement',
    ],
  },
  {
    icon: BuildingOffice2Icon,
    title: 'Regional Warehousing & Staging',
    points: [
      'Regional stock positioning near deployment sites',
      'Equipment inspection and pre-configuration',
      'Pre-installation preparation and testing',
      'Flexible redistribution across locations',
    ],
  },
  {
    icon: CubeTransparentIcon,
    title: 'White Glove Delivery & On-Site Coordination',
    points: [
      'Scheduled delivery to exact specifications',
      'Inside delivery and precise equipment placement',
      'Installation support and coordination',
      'Packaging removal and site cleanup',
    ],
  },
];

const whyChooseCards = [
  {
    icon: GlobeAltIcon,
    title: 'Single Global Partner',
    description: 'One accountable execution layer across 140+ countries, proven in 42-country rollouts.',
    accent: 'bg-blue-500',
  },
  {
    icon: ShieldCheckIcon,
    title: 'SLA-Driven Execution',
    description: 'Structured compliance management and delivery timelines that protect your service commitments.',
    accent: 'bg-cyan-500',
  },
  {
    icon: ChartBarIcon,
    title: 'Full Visibility & Control',
    description: 'Centralised reporting and tracking across every region, every shipment, every milestone.',
    accent: 'bg-indigo-500',
  },
];

const GlobalEnterprises: React.FC = () => (
  <SolutionPageTemplate
    eyebrow="Global Enterprises"
    title="Enterprise ICT Logistics, Simplified"
    highlight="Simplified"
    description="One execution partner for your global ICT infrastructure rollouts, from compliance to final installation."
    heroIcon={BuildingLibraryIcon}
    heroAccentIcon={GlobeAltIcon}
    problemHeading="The challenges you face"
    problemDescription="Enterprise ICT rollouts across multiple countries create compounding complexity at every stage of execution."
    painPoints={painPoints}
    solutionsIntro="TrueLog builds a single, predictable execution layer across every stage of your rollout."
    solutions={solutions}
    whyChooseCards={whyChooseCards}
    ctaTitle="Ready to simplify your global ICT rollouts?"
    ctaDescription="Let TrueLog be your single execution partner, from compliance and warehousing to on-site delivery in 140+ countries."
  />
);

export default GlobalEnterprises;
